import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { SaveDialogContent, SaveForm, SaveButton } from "@/components/SaveForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import "./PlatformTeam.css";

const empty = { name: "", email: "", password: "" };
export default function PlatformTeam() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const owner = user?.is_platform_owner === true;
  const load = async () => {
    try { const { data } = await api.get("/platform/team"); setMembers(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (owner) load(); }, [owner]);
  const close = () => { setOpen(false); setEditing(null); setForm(empty); };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.patch("/platform/team/" + editing.id, { name: form.name });
      else await api.post("/platform/team", form);
      close(); await load(); toast.success("Integrante salvo.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const changeAccess = async () => {
    if (busy || !confirm) return;
    setBusy(true);
    try {
      if (confirm.action === "delete") await api.delete("/platform/team/" + confirm.id);
      else if (confirm.action === "restore") await api.post("/platform/team/" + confirm.id + "/restore");
      else await api.patch("/platform/team/" + confirm.id, { active: !confirm.active });
      setConfirm(null); await load(); toast.success("Integrante atualizado.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  if (!owner) return <p>Acesso exclusivo do proprietário.</p>;
  return <div className="platform-team space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="font-display text-3xl">Equipe da plataforma</h1>
        <p className="text-slate-400">Contas individuais para administrar empresas, planos e assinaturas.</p></div>
      <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }}>Novo integrante</Button>
    </div>
    <div className="rounded-lg border border-violet-800 bg-violet-950/20 p-4 text-sm">
      <strong>Você é o proprietário.</strong> Somente você gerencia esta equipe, pagamentos e exclusões.
      <p className="mt-2 font-medium text-violet-200">Proprietário protegido: seu acesso não é alterado nesta lista.</p>
      <p className="mt-2 text-slate-300">A equipe consulta valores e pagamentos e edita nomes e descrições dos módulos, sem acessar o estoque das empresas. Alterações financeiras, ativação global e gestão desta equipe permanecem com você.</p>
    </div>
    {loading ? <p>Carregando equipe...</p> : <div className="overflow-x-auto rounded-lg border border-slate-700">
      <table className="team-table w-full text-sm"><thead><tr><th>Nome</th><th>E-mail</th><th>Status</th><th>Ações</th></tr></thead>
        <tbody>{members.map(member => <tr key={member.id}>
          <td data-label="Nome">{member.name}</td><td data-label="E-mail" className="team-email">{member.email}</td><td data-label="Status"><span className={member.active ? "team-status team-status-active" : "team-status"}>{member.deleted_at ? "Excluído" : member.active ? "Ativo" : "Inativo"}</span></td>
          <td data-label="Ações"><div className="team-actions">
            {member.deleted_at ? <Button type="button" variant="outline" className="team-action team-action-reactivate" disabled={busy} onClick={() => setConfirm({ ...member, action: "restore" })}>Restaurar</Button> : <>
            <Button type="button" variant="outline" className="team-action team-action-edit" onClick={() => { setEditing(member); setForm({ ...empty, name: member.name }); setOpen(true); }}>Editar nome</Button>
            <Button type="button" variant="outline" className={member.active ? "team-action team-action-deactivate" : "team-action team-action-reactivate"} disabled={busy} onClick={() => setConfirm(member)}>{member.active ? "Desativar" : "Reativar"}</Button>
            <Button type="button" variant="outline" className="team-action team-action-deactivate" disabled={busy} onClick={() => setConfirm({ ...member, action: "delete" })}>Excluir</Button>
            </>}
          </div></td>
        </tr>)}</tbody></table>
      {!members.length && <p className="p-5 text-slate-400">Nenhum integrante cadastrado.</p>}
    </div>}
    <Dialog open={open} onOpenChange={value => { if (!value && !busy) close(); }}>
      <SaveDialogContent saveBlocked={busy} className="bg-slate-900 text-slate-100">
        <SaveForm onSave={save} disabled={busy} className="space-y-4">
          <DialogHeader><DialogTitle>{editing ? "Editar integrante" : "Novo integrante"}</DialogTitle></DialogHeader>
          <div><Label htmlFor="team-name">Nome</Label><Input id="team-name" required maxLength={120} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          {!editing && <><div><Label htmlFor="team-email">E-mail individual</Label><Input id="team-email" type="email" autoComplete="off" required maxLength={254} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label htmlFor="team-password">Senha inicial</Label><Input id="team-password" type="password" autoComplete="new-password" required minLength={6} maxLength={128} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              <p className="text-xs text-slate-400 mt-1">Use uma senha exclusiva. Nenhum convite será enviado. O integrante pode alterar sua senha após entrar.</p></div></>}
          <DialogFooter><Button type="button" variant="ghost" disabled={busy} onClick={close}>Cancelar</Button><SaveButton type="submit" disabled={busy}>{busy ? "Salvando..." : "Salvar integrante"}</SaveButton></DialogFooter>
        </SaveForm>
      </SaveDialogContent>
    </Dialog>
    <Dialog open={!!confirm} onOpenChange={value => { if (!value && !busy) setConfirm(null); }}>
      <DialogContent className="bg-slate-900 text-slate-100" onKeyDown={event => { if (event.key === "Enter") event.preventDefault(); }}>
        <DialogHeader><DialogTitle>{confirm?.action === "delete" ? "Excluir integrante" : confirm?.action === "restore" ? "Restaurar integrante" : confirm?.active ? "Desativar acesso" : "Reativar acesso"}</DialogTitle></DialogHeader>
        <p>{confirm?.name}: {confirm?.action === "delete" ? "o acesso será bloqueado e as sessões revogadas. O histórico será preservado e a exclusão poderá ser desfeita." : confirm?.action === "restore" ? "o cadastro será restaurado como inativo. Reative separadamente para permitir acesso." : confirm?.active ? "as sessões atuais serão revogadas." : "a conta poderá entrar novamente."}</p>
        <DialogFooter><Button disabled={busy} variant="ghost" onClick={() => setConfirm(null)}>Cancelar</Button><Button disabled={busy} onClick={changeAccess}>{confirm?.action === "delete" ? "Confirmar exclusão" : confirm?.action === "restore" ? "Confirmar restauração" : "Confirmar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}
